/**
 * Small pictures of the things that can be put in the world.
 *
 * A list of names tells somebody what a thing is called, not what it looks like,
 * and a browser of twenty names is twenty guesses. These are the actual shapes,
 * drawn once each by the same code the world draws them with, and kept as images
 * so the browser costs nothing to scroll.
 *
 * One renderer, one scene, reused for every shape: making twenty renderers is
 * how a page runs out of WebGL contexts.
 */

import * as THREE from 'three';
import { shapeParts } from './scene';

const SIZE = 96;

const cache = new Map<number, string>();

let renderer: THREE.WebGLRenderer | null = null;
let scene: THREE.Scene | null = null;
let camera: THREE.PerspectiveCamera | null = null;

function ready(): boolean {
  if (renderer) return true;

  try {
    renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true,
    });
    renderer.setSize(SIZE, SIZE);
    renderer.setClearColor(0x000000, 0);

    scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 0.85));

    const sun = new THREE.DirectionalLight(0xffffff, 0.9);
    sun.position.set(2, 4, 3);
    scene.add(sun);

    camera = new THREE.PerspectiveCamera(35, 1, 0.1, 40);
    return true;
  } catch {
    // No WebGL to spare. The browser falls back to names, which is what it was.
    renderer = null;
    return false;
  }
}

/**
 * A picture of one kind of scenery, as a data URL.
 *
 * Null when there is nothing to draw it with, so the caller can fall back rather
 * than showing an empty box.
 */
export function thumbnailFor(objectId: number, looksLike = 0): string | null {
  const held = cache.get(objectId);
  if (held !== undefined) return held;
  if (!ready() || !renderer || !scene || !camera) return null;

  const group = new THREE.Group();
  for (const part of shapeParts(looksLike > 0 ? looksLike : objectId)) {
    const mesh = new THREE.Mesh(part.geometry(), part.material());
    mesh.position.y = part.offset * part.scale;
    mesh.scale.setScalar(part.scale);
    group.add(mesh);
  }

  scene.add(group);

  // Framed on whatever it turned out to be, so a fence post and a tree both fill
  // the picture rather than one of them being a speck.
  const bounds = new THREE.Box3().setFromObject(group);
  const middle = bounds.getCenter(new THREE.Vector3());
  const reach = Math.max(bounds.getSize(new THREE.Vector3()).length(), 0.6);

  camera.position.set(middle.x + reach * 0.9, middle.y + reach * 0.75, middle.z + reach * 0.9);
  camera.lookAt(middle);

  renderer.render(scene, camera);
  const url = renderer.domElement.toDataURL('image/png');

  scene.remove(group);
  for (const child of group.children) {
    const mesh = child as THREE.Mesh;
    mesh.geometry.dispose();
    (mesh.material as THREE.Material).dispose();
  }

  cache.set(objectId, url);
  return url;
}
